import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { forexApi, type OwnSignalExecutionTargetDTO } from '@/services/forexApi';
import { ownExecutionLabel } from '@/lib/followPresentation';

export default function ExecutionLogPage() {
  const [params] = useSearchParams();
  const signalId = params.get('signal_id');
  const { accessToken } = useAuth();
  const [items, setItems] = useState<OwnSignalExecutionTargetDTO[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadedFor, setLoadedFor] = useState('');
  const sequence = useRef(0);
  const inFlight = useRef(false);
  const scope = `${accessToken || ''}:${signalId || ''}`;
  const visibleItems = loadedFor === scope ? items : [];
  const cursor = loadedFor === scope ? nextCursor : null;
  const load = useCallback(async (after?: string) => {
    if (!signalId || inFlight.current) return;
    inFlight.current = true;
    const request = ++sequence.current;
    setLoading(true);
    setError('');
    try {
      const page = await forexApi.listOwnSignalExecution(signalId, accessToken, after);
      if (request !== sequence.current) return;
      setItems(current => after
        ? [...new Map([...current, ...page.items].map(item => [item.target_id, item])).values()]
        : page.items);
      setNextCursor(page.next_cursor);
      setLoadedFor(scope);
    } catch {
      if (request !== sequence.current) return;
      setError(after ? '后续账号结果加载失败，已加载结果保留。' : '结果暂不可用，或你无权查看该信号。已有结果可能不是最新状态。');
    } finally {
      if (request === sequence.current) { inFlight.current = false; setLoading(false); }
    }
  }, [signalId, accessToken, scope]);

  useEffect(() => {
    inFlight.current = false;
    setItems([]); setNextCursor(null); setLoadedFor('');
    void load();
    return () => { sequence.current += 1; inFlight.current = false; };
  }, [load]);

  if (!signalId) return <Navigate to="/trade-history" replace />;
  return <main className="min-h-screen bg-[#F7F8FC] px-5 pb-28 pt-[max(20px,env(safe-area-inset-top))]" aria-busy={loading}>
    <Link className="inline-flex min-h-11 items-center text-sm text-indigo-600" to="/ai-decisions">返回决策记录</Link>
    <h1 className="mt-3 text-2xl font-bold text-gray-900">我的账户执行</h1>
    <p className="mt-2 text-sm leading-relaxed text-gray-500">只展示本人账户。指令已发送或 EA 已接收，均不代表成交。</p>
    <button type="button" disabled={loading} onClick={() => void load()} className="my-4 min-h-11 rounded-xl bg-indigo-600 px-5 text-sm font-medium text-white disabled:opacity-50">{loading ? '正在读取…' : '刷新结果'}</button>
    {error && <div role="alert" className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{error}</div>}
    {loading && !visibleItems.length && <p role="status" className="rounded-2xl bg-white p-5 text-sm text-gray-500">正在加载账户结果…</p>}
    <div className="space-y-4">
      {visibleItems.map(item => <article key={item.target_id} className="rounded-2xl bg-white p-5">
        <p className="break-all text-sm font-medium text-gray-500">账户 {item.account_scope}</p>
        <h2 className="mt-3 text-lg font-semibold text-gray-900">{ownExecutionLabel(item)}</h2>
        {item.readiness_reason && <p className="mt-3 break-words text-sm leading-relaxed text-gray-600">{item.readiness_reason}</p>}
        <p className="mt-4 text-xs text-gray-500">最后更新：{Number.isFinite(item.updated_at) && item.updated_at > 0
          ? new Date(item.updated_at < 1e12 ? item.updated_at * 1000 : item.updated_at).toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' }) + '（UTC+8）' : '—'}</p>
      </article>)}
    </div>
    {!loading && !error && !visibleItems.length && <section className="rounded-2xl bg-white p-5"><h2 className="font-semibold">暂无本人账户结果</h2><p className="mt-2 text-sm text-gray-500">当前信号还没有可展示的本人执行记录。</p></section>}
    {cursor && <button type="button" disabled={loading} onClick={() => void load(cursor)} className="mt-4 min-h-11 w-full rounded-xl border border-indigo-200 bg-white px-4 text-sm text-indigo-600 disabled:opacity-50">{error ? '重试加载更多账户' : '加载更多账户'}</button>}
  </main>;
}
